import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import { PiCalendarBlank, PiCaretDown, PiEye, PiEyeSlash } from 'react-icons/pi';
import MoneyInput from '../MoneyInput';
import './styles.css';

const FieldLayout = ({ id, label, hint, error, required, size = 'md', children }) => {
  const generatedId = useId();
  const controlId = id || `nf-field-${generatedId}`;
  const hintId = hint && !error ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={`nf-field nf-field--${size}`}>
      <label className="nf-field__label" htmlFor={controlId}>
        {label}
        {required && <span className="nf-field__required" aria-hidden="true"> *</span>}
      </label>
      {children({ controlId, describedBy })}
      {hint && !error && <span className="nf-field__hint" id={hintId}>{hint}</span>}
      {error && <span className="nf-field__error" id={errorId} role="alert">{error}</span>}
    </div>
  );
};

const FieldControl = ({
  id,
  label,
  hint,
  error,
  required,
  disabled,
  readOnly,
  size,
  value,
  onChange,
  icon,
  trailingIcon,
  trailingAction,
  className = '',
  ...inputProps
}) => (
  <FieldLayout id={id} label={label} hint={hint} error={error} required={required} size={size}>
    {({ controlId, describedBy }) => (
      <div className={`nf-field__control-shell ${error ? 'nf-field__control-shell--error' : ''} ${disabled ? 'nf-field__control-shell--disabled' : ''} ${readOnly ? 'nf-field__control-shell--readonly' : ''}`}>
        {icon && <span className="nf-field__icon" aria-hidden="true">{icon}</span>}
        <input
          {...inputProps}
          id={controlId}
          className={`nf-field__control ${value ? 'nf-field__control--filled' : ''} ${className}`}
          value={value ?? ''}
          onChange={(event) => onChange?.(event.target.value)}
          required={required}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={describedBy}
        />
        {trailingIcon && <span className="nf-field__trailing-icon" aria-hidden="true">{trailingIcon}</span>}
        {trailingAction}
      </div>
    )}
  </FieldLayout>
);

export const TextField = (props) => <FieldControl type="text" {...props} />;

export const PasswordField = (props) => {
  const [isVisible, setIsVisible] = useState(false);
  const toggleLabel = isVisible ? 'Ocultar senha' : 'Mostrar senha';

  return (
    <FieldControl
      type={isVisible ? 'text' : 'password'}
      autoComplete="current-password"
      trailingAction={(
        <button
          type="button"
          className="nf-field__password-toggle"
          onClick={() => setIsVisible((visible) => !visible)}
          aria-label={toggleLabel}
          title={toggleLabel}
        >
          {isVisible ? <PiEyeSlash size={20} /> : <PiEye size={20} />}
        </button>
      )}
      {...props}
    />
  );
};

export const MoneyField = ({ hint = 'Digite o valor em reais; use vírgula para os centavos.', ...props }) => (
  <FieldLayout {...props} hint={hint}>
    {({ controlId, describedBy }) => (
      <div className={`nf-field__control-shell nf-field__control-shell--money ${props.error ? 'nf-field__control-shell--error' : ''} ${props.disabled ? 'nf-field__control-shell--disabled' : ''} ${props.readOnly ? 'nf-field__control-shell--readonly' : ''}`}>
        <span className="nf-field__prefix" aria-hidden="true">R$</span>
        <MoneyInput
          id={controlId}
          className={`nf-field__control nf-field__control--money ${props.value ? 'nf-field__control--filled' : ''}`}
          placeholder={props.placeholder}
          name={props.name}
          value={props.value ?? ''}
          onChange={props.onChange}
          required={props.required}
          disabled={props.disabled}
          readOnly={props.readOnly}
          aria-invalid={props.error ? 'true' : 'false'}
          aria-describedby={describedBy}
        />
      </div>
    )}
  </FieldLayout>
);

export const DateField = (props) => (
  <FieldControl type="date" icon={<PiCalendarBlank size={20} />} className="nf-field__control--native-picker" {...props} />
);

export const SelectField = ({
  id,
  label,
  hint,
  error,
  required,
  disabled,
  readOnly,
  size = 'md',
  value,
  onChange,
  children,
  name,
}) => (
  <FieldLayout id={id} label={label} hint={hint} error={error} required={required} size={size}>
    {({ controlId, describedBy }) => (
      <div className={`nf-field__control-shell ${error ? 'nf-field__control-shell--error' : ''} ${disabled || readOnly ? 'nf-field__control-shell--disabled' : ''}`}>
        <select
          id={controlId}
          name={name}
          className={`nf-field__control nf-field__control--select ${value ? 'nf-field__control--filled' : ''}`}
          value={value ?? ''}
          onChange={(event) => onChange?.(event.target.value)}
          required={required}
          disabled={disabled || readOnly}
          aria-readonly={readOnly ? 'true' : undefined}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={describedBy}
        >
          {children}
        </select>
        <span className="nf-field__trailing-icon" aria-hidden="true"><PiCaretDown size={18} data-testid="select-chevron" /></span>
      </div>
    )}
  </FieldLayout>
);

const commonPropTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md']),
  value: PropTypes.string,
  onChange: PropTypes.func,
  name: PropTypes.string,
};

FieldLayout.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  error: PropTypes.string,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md']),
  children: PropTypes.func.isRequired,
};

FieldControl.propTypes = {
  ...commonPropTypes,
  icon: PropTypes.node,
  trailingIcon: PropTypes.node,
  trailingAction: PropTypes.node,
  className: PropTypes.string,
};
TextField.propTypes = commonPropTypes;
PasswordField.propTypes = commonPropTypes;
MoneyField.propTypes = commonPropTypes;
DateField.propTypes = commonPropTypes;
SelectField.propTypes = { ...commonPropTypes, children: PropTypes.node.isRequired };